import { RegisterClass } from "core/Misc/typeStore";
import { PropertyTypeForEdition, editableInPropertyPage } from "core/Decorators/nodeDecorator";
import { RewardGraphNode } from "../rewardGraphNode";
import { RewardGraphConnectionPointTypes } from "../rewardGraphConnectionPointTypes";
import { RewardGraphConnectionPoint } from "../rewardGraphConnectionPoint";
import { RewardGraphBuildState } from "../rewardGraphBuildState";

/**
 * Conditions supported by the condition block
 */
export enum RewardLogicBlockTests {
    /** Logical Exclusive OR */
    Xor,
    /** Logical Or */
    Or,
    /** Logical And */
    And,
}

function OperationString(operation : RewardLogicBlockTests) : string {
    if( typeof operation === 'number' )
        return RewardLogicBlockTests[operation];
    return operation as string;
}

function StringOperation(op : string) {
    return (RewardLogicBlockTests as any)[op];
}

/**
 * Block used to evaluate a condition and return a true or false value
 */
export class RewardLogicBlock extends RewardGraphNode {
    /**
     * Gets or sets the test used by the block
     */
    @editableInPropertyPage("Test", PropertyTypeForEdition.List, "ADVANCED", {
        notifiers: { rebuild: true },
        options: [
            { label: "Xor", value: RewardLogicBlockTests.Xor },
            { label: "Or", value: RewardLogicBlockTests.Or },
            { label: "And", value: RewardLogicBlockTests.And },
        ],
    })
    public test = RewardLogicBlockTests.And;

    /**
     * Create a new ConditionBlock
     * @param name defines the block name
     */
    public constructor(name: string) {
        super(name);

        this.registerInput("left", RewardGraphConnectionPointTypes.Boolean);
        this.registerInput("right", RewardGraphConnectionPointTypes.Boolean, true, 0);
        this.registerInput("ifTrue", RewardGraphConnectionPointTypes.AutoDetect, true, 1);
        this.registerInput("ifFalse", RewardGraphConnectionPointTypes.AutoDetect, true, 0);

        this.registerOutput("output", RewardGraphConnectionPointTypes.BasedOnInput);

        this._outputs[0]._typeConnectionSource = this._inputs[2];
        this._outputs[0]._defaultConnectionPointType = RewardGraphConnectionPointTypes.Boolean;
        this._linkConnectionTypes(2, 3);
    }

    /**
     * Gets the current class name
     * @returns the class name
     */
    public override getClassName() {
        return "RewardLogicBlock";
    }

    /**
     * Gets the left input component
     */
    public get left(): RewardGraphConnectionPoint {
        return this._inputs[0];
    }

    /**
     * Gets the right input component
     */
    public get right(): RewardGraphConnectionPoint {
        return this._inputs[1];
    }

    /**
     * Gets the ifTrue input component
     */
    public get ifTrue(): RewardGraphConnectionPoint {
        return this._inputs[2];
    }

    /**
     * Gets the ifFalse input component
     */
    public get ifFalse(): RewardGraphConnectionPoint {
        return this._inputs[3];
    }

    /**
     * Gets the output component
     */
    public get output(): RewardGraphConnectionPoint {
        return this._outputs[0];
    }

    protected override _buildBlock() {
        if (!this.left.isConnected) {
            this.output._storedFunction = null;
            this.output._storedValue = null;
            return;
        }

        const func = (state: RewardGraphBuildState) => {
            const left = this.left.getConnectedValue(state) as number;
            const right = this.right.getConnectedValue(state) as number;
            let condition = false;

            switch (this.test) {
                case RewardLogicBlockTests.Xor:
                    condition = (!!left && !right) || (!left && !!right);
                    break;
                case RewardLogicBlockTests.Or:
                    condition = !!left || !!right;
                    break;
                case RewardLogicBlockTests.And:
                    condition = !!left && !!right;
                    break;
            }
            return condition;
        };

        this.output._storedFunction = (state) => {
            if (func(state)) {
                return this.ifTrue.getConnectedValue(state);
            }

            return this.ifFalse.getConnectedValue(state);
        };
    }

    protected override _dumpPropertiesCode() {
        const codeString = super._dumpPropertiesCode() + `${this._codeVariableName}.test = MELS.RewardLogicBlock.${RewardLogicBlockTests[this.test]};\n`;
        return codeString;
    }

    /**
     * Serializes this block in a JSON representation
     * @returns the serialized block object
     */
    public override serialize(): any {
        const serializationObject = super.serialize();

        serializationObject.test = OperationString(this.test);

        return serializationObject;
    }

    public override _deserialize(serializationObject: any) {
        super._deserialize(serializationObject);

        this.test = StringOperation(serializationObject.test);
    }
}

RegisterClass("MELS.RewardLogicBlock", RewardLogicBlock);
